import React, { useState } from "react";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Send, 
  MessageSquare, 
  Building, 
  Users,
  ArrowRight,
  CheckCircle,
  Globe,
  Clock
} from "lucide-react";
import axios from "axios";
import Select from "react-select";
import ReCAPTCHA from "react-google-recaptcha";

function Contact() {
  const [formData, setFormData] = useState({
    enquiry: [],
    name: "",
    mobile: "",
    email: "",
    city: "",
    pincode: "",
    message: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [responseMessage, setResponseMessage] = useState("");

  const [recaptchaToken, setRecaptchaToken] = useState(null);
 const [errors, setErrors] = useState({});

  // reCAPTCHA site key from .env (replace with your actual site key)
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "your-site-key-here";
  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    setErrors((prev) => ({ ...prev, recaptcha: "" }));
  };

  const enquiryOptions = [
    { value: "LED_lights", label: "LED Lights" },
    { value: "Lithium_Battery", label: "Lithium Battery" },
    { value: "Inverter_And_UPS", label: "Inverter & UPS" },
    { value: "Metal_Engineering_Profile", label: "Metal Engineering Profile" },
    { value: "other", label: "Other" },
  ];

  const validateForm = () => {
    let errors = {};
    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.mobile.match(/^[6-9]\d{9}$/))
      errors.mobile = "Enter a valid 10-digit mobile number starting with 6-9";
    if (!formData.email.match(/^\S+@\S+\.\S+$/))
      errors.email = "Enter a valid email address";
    if (!formData.city.trim()) errors.city = "City is required";
    if (!formData.pincode.match(/^\d{6}$/))
      errors.pincode = "Enter a valid 6-digit pincode";
    if (!formData.message.trim()) errors.message = "Message is required";
    if (formData.enquiry.length === 0)
      errors.enquiry = "Please select at least one enquiry type";
    if (!recaptchaToken) {
      errors.recaptcha = "Please complete the reCAPTCHA";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleEnquiryChange = (selectedOptions) => {
    const selectedValues = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    setFormData({ ...formData, enquiry: selectedValues });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Call validation before submission
    if (!validateForm()) {
      return; // Stop submission if validation fails
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_Backend_URL}/contact/contactsubmit`,
        formData
      );
      setResponseMessage(response.data.message);
      setFormData({
        enquiry: [],
        name: "",
        mobile: "",
        email: "",
        city: "",
        pincode: "",
        message: "",
      });
      setFormErrors({}); 
    } catch (error) {
      setResponseMessage("Error submitting the form. Please try again.");
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone Number",
      content: "+91 97780-44000, 97785-44000",
      gradient: "from-blue-500 to-indigo-600"
    },
    {
      icon: Mail,
      title: "Email Address", 
      content: "sales@spinoff.in",
      gradient: "from-purple-500 to-pink-600"
    },
    {
      icon: MapPin,
      title: "Corporate Office",
      content: "SCO-117/118, Sec-43B, Chandigarh-160022",
      gradient: "from-green-500 to-teal-600"
    },
    {
      icon: Clock,
      title: "Business Hours",
      content: "Mon - Sat: 9:00 AM - 6:00 PM",
      gradient: "from-orange-500 to-red-600"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50" style={{marginTop:'30px'}}>
      {/* Hero Section - Rich with Colors and Effects */}
      <section className="pt-24 pb-16 px-4 sm:px-6 text-center relative overflow-hidden">
        {/* Rich Background Layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-100 via-white to-blue-100 opacity-60"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-100/40 via-transparent to-transparent"></div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          {/* Enhanced Feature Pill with Gradient */}
          <div className="inline-flex items-center gap-2 px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full text-xs sm:text-sm font-medium text-white mb-6 sm:mb-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
            <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-300" />
            <span className="bg-gradient-to-r from-yellow-200 to-yellow-100 bg-clip-text text-transparent font-bold">Quick</span> Available Support
            <Send className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-300 animate-pulse" />
          </div>

          {/* Rich Main Headline with Multiple Gradients */}
          <h1 className="text-4xl sm:text-5xl md:text-5xl font-extrabold tracking-tighter mb-6 sm:mb-8 leading-tight">
            <span className="bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-900 bg-clip-text text-transparent">Get In Touch With</span>
            <br />
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">Spinoff</span>
          </h1>

          {/* Enhanced Subtext with Gradient */}
          <p className="text-lg sm:text-xl text-slate-600 mb-12 sm:mb-16 max-w-4xl mx-auto leading-relaxed bg-gradient-to-r from-slate-600 to-slate-500 bg-clip-text text-transparent px-4">
            We're here to help and answer any questions you might have. 
            We look forward to hearing from you and building lasting partnerships.
          </p>

          {/* Rich Contact Cards with Gradient Effects */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto mb-16 sm:mb-20">
            <div className="group relative bg-gradient-to-br from-white to-blue-50 border border-blue-200/50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-purple-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Phone className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                <div className="text-lg sm:text-xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">Call Us</div>
                <div className="text-slate-600 text-sm sm:text-base font-semibold">+91 97780-44000</div>
              </div>
              <div className="absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-br from-blue-400 to-purple-600 rounded-full"></div>
            </div>
            
            <div className="group relative bg-gradient-to-br from-white to-emerald-50 border border-emerald-200/50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/10 to-teal-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Mail className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                <div className="text-lg sm:text-xl font-black bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-2">Email Us</div>
                <div className="text-slate-600 text-sm sm:text-base font-semibold">sales@spinoff.in</div>
              </div>
              <div className="absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-br from-emerald-400 to-teal-600 rounded-full"></div>
            </div>
            
            <div className="group relative bg-gradient-to-br from-white to-rose-50 border border-rose-200/50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-rose-400/10 to-pink-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300">
                  <MapPin className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                <div className="text-lg sm:text-xl font-black bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent mb-2">Visit Us</div>
                <div className="text-slate-600 text-sm sm:text-base font-semibold">Chandigarh, India</div>
              </div>
              <div className="absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-br from-rose-400 to-pink-600 rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6 lg:p-8">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Send us a message</h2>
                
                {responseMessage && (
                  <div className={`p-3 sm:p-4 rounded-lg mb-4 sm:mb-6 ${
                    responseMessage.includes("Error") 
                      ? "bg-red-50 border border-red-200 text-red-700" 
                      : "bg-green-50 border border-green-200 text-green-700"
                  }`}>
                    {responseMessage}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        Select Enquiry Type *
                      </label>
                      <Select
                        isMulti
                        name="enquiry"
                        options={enquiryOptions}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        onChange={handleEnquiryChange}
                        value={enquiryOptions.filter((option) =>
                          formData.enquiry.includes(option.value)
                        )}
                        placeholder="Select enquiry types..."
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: formErrors.enquiry ? '#ef4444' : '#d1d5db',
                            '&:hover': { borderColor: '#6366f1' },
                            boxShadow: 'none'
                          })
                        }}
                      />
                      {formErrors.enquiry && (
                        <span className="text-red-500 text-xs sm:text-sm mt-1">{formErrors.enquiry}</span>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        Full Name *
                      </label>
                      <input
                        name="name"
                        type="text"
                        required
                        className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base ${
                          formErrors.name ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleChange}
                      />
                      {formErrors.name && (
                        <span className="text-red-500 text-xs sm:text-sm mt-1">{formErrors.name}</span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        Email Address *
                      </label>
                      <input
                        name="email"
                        type="email"
                        required
                        className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base ${
                          formErrors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={handleChange}
                      />
                      {formErrors.email && (
                        <span className="text-red-500 text-xs sm:text-sm mt-1">{formErrors.email}</span>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        Mobile Number *
                      </label>
                      <input
                        name="mobile"
                        type="tel"
                        required
                        className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base ${
                          formErrors.mobile ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="+91 98765 43210"
                        value={formData.mobile}
                        onChange={handleChange}
                      />
                      {formErrors.mobile && (
                        <span className="text-red-500 text-xs sm:text-sm mt-1">{formErrors.mobile}</span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        City *
                      </label>
                      <input
                        name="city"
                        type="text"
                        required
                        className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base ${
                          formErrors.city ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Chandigarh"
                        value={formData.city}
                        onChange={handleChange}
                      />
                      {formErrors.city && (
                        <span className="text-red-500 text-xs sm:text-sm mt-1">{formErrors.city}</span>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        Pincode *
                      </label>
                      <input
                        name="pincode"
                        type="text"
                        required
                        className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base ${
                          formErrors.pincode ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="160022"
                        value={formData.pincode}
                        onChange={handleChange}
                      />
                      {formErrors.pincode && (
                        <span className="text-red-500 text-xs sm:text-sm mt-1">{formErrors.pincode}</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      required
                      className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none text-sm sm:text-base ${
                        formErrors.message ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Tell us about your requirements..."
                      value={formData.message}
                      onChange={handleChange}
                    />
                    {formErrors.message && (
                      <span className="text-red-500 text-xs sm:text-sm mt-1">{formErrors.message}</span>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <ReCAPTCHA
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={handleRecaptchaChange}
                        theme="light"
                      />
                      {errors.recaptcha && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.recaptcha}</p>}
                    </div>
                    
                    <button
                      type="submit"
                      className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
                    >
                      <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                      Send Message
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Contact Information */}
            <div className="lg:col-span-1">
              <div className="space-y-4 sm:space-y-6">
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Contact Information</h3>
                  
                  <div className="space-y-4 sm:space-y-6">
                    {contactInfo.map((info, index) => {
                      const Icon = info.icon;
                      return (
                        <div key={index} className="flex items-start gap-3 sm:gap-4">
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br ${info.gradient} rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0`}>
                            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">{info.title}</h4>
                            <p className="text-gray-600 text-xs sm:text-sm">{info.content}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 text-white">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <Clock className="w-5 h-5 sm:w-6 sm:h-6" />
                    <h3 className="text-lg sm:text-xl font-bold">Quick Response</h3>
                  </div>
                  <p className="text-blue-100 mb-3 sm:mb-4 text-sm sm:text-base">
                    Our team responds quickly to all inquiries.
                  </p>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-300" />
                    <span className="text-xs sm:text-sm">Quick Support Available</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Visit Our Offices</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base px-4">
              Visit our manufacturing facilities for a personal consultation with our experts.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {/* Spinoff Technopark Office */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-gray-100">
                <div className="flex items-center mb-2">
                  <Building className="w-5 h-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Finike Lights manufacturing center</h3>
                </div>
             
              </div>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d13721.3065930351!2d76.6931899!3d30.7092162!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390feeff34a1a62b%3A0x4cdbc1aa576f2c91!2sSpinoff%20Technopark%20Pvt%20Ltd!5e0!3m2!1sen!2sin!4v1723268717290!5m2!1sen!2sin"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full"
              />
            </div>

            {/* Finike Lithium Office */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-gray-100">
                <div className="flex items-center mb-2">
                  <Building className="w-5 h-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Finike Lithium manufacturing center</h3>
                </div>
               
              </div>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2465.774112071921!2d76.5235070738475!3d30.52475859565715!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390fdc0cebe23ddb%3A0x11278769d11ae5fa!2sFinike%20Lithium%20-%20Lithium-Ion%20Battery%20Manufacturer!5e1!3m2!1sen!2sin!4v1774439440648!5m2!1sen!2sin"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full"
              />
            </div>
          </div>

          {/* Additional Location Info */}
          <div className="mt-8 sm:mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center mb-4">
                <Clock className="w-6 h-6 text-blue-600 mr-3" />
                <h4 className="text-lg font-semibold text-gray-900">Working Hours</h4>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <p><span className="font-medium">Monday - Saturday:</span> 9:00 AM - 6:00 PM</p>
                <p><span className="font-medium">Sunday:</span> Closed</p>
                <p><span className="font-medium">Lunch Break:</span> 1:00 PM - 2:00 PM</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center mb-4">
                <Globe className="w-6 h-6 text-blue-600 mr-3" />
                <h4 className="text-lg font-semibold text-gray-900">Get Directions</h4>
              </div>
              <div className="space-y-3">
                <a 
                  href="https://maps.google.com/?q=Spinoff+Technopark+Pvt+Ltd+Mohali"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <span className="text-sm font-medium text-blue-700">Directions to Spinoff Technopark</span>
                  <ArrowRight className="w-4 h-4 text-blue-600" />
                </a>
                <a 
                  href="https://maps.google.com/?q=Finike+Lithium+Rajpura"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <span className="text-sm font-medium text-blue-700">Directions to Finike Lithium</span>
                  <ArrowRight className="w-4 h-4 text-blue-600" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
